import { Button, Card, Descriptions, Modal, message, Spin } from "antd";
import React, { Fragment } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import entitlements from "../config/entitlements";
import { AssetAllocationRate } from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import {
  DELETE_ASSET_ALLOCATION_RATE,
  GET_ASSET_ALLOCATION_RATE,
} from "../gql/economicIndicators";

interface RouteParams {
  id: string;
}

interface State {
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  assetAllocationRate?: AssetAllocationRate;
}

function ModalWrapper(
  showDialog: boolean,
  deleteAssetAllocationRate: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteAssetAllocationRate}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this asset allocation rate?</div>
    </Modal>
  );
}

export default class ViewAssetAllocationRate extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      showDialog: false,
      assetAllocationRate: undefined,
    };
    this.deleteAssetAllocationRate = this.deleteAssetAllocationRate.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getAssetAllocationRate();
    });
  }

  getAssetAllocationRate = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_ASSET_ALLOCATION_RATE,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const assetAllocationRate = data.data?.assetAllocationRate;
            this.setState({
              assetAllocationRate: assetAllocationRate,
            });
          } else {
            message.error("Failed to get asset allocation rate data");
          }
        });
      },
    );
  };

  deleteAssetAllocationRate(): void {
    const { id } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_ASSET_ALLOCATION_RATE,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data?.deleteAssetAllocationRate.result) {
              message.success("Asset allocation rate deleted successfuly");
              this.redirectToAllAssetAllocationRatesPage();
            } else {
              message.error("Failed to delete asset allocation rate");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllAssetAllocationRatesPage(): void {
    this.props.history.push("/asset-allocation-rates");
  }

  render(): JSX.Element | null {
    const { assetAllocationRate, isLoading, id, showDialog } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {ModalWrapper(
                showDialog,
                this.deleteAssetAllocationRate,
                this.toggleDeleteConfirmationModal,
              )}
              <Descriptions
                extra={
                  <div>
                    <EntitledSection
                      entitlement={entitlements.EconomicIndicators.edit}
                    >
                      <NavLink to={`/asset-allocation-rates/${id}/edit`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "  rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-asset-allocation-rate-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    <EntitledSection
                      entitlement={entitlements.EconomicIndicators.delete}
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-asset-allocation-rate-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection>
                  </div>
                }
              >
                <Descriptions.Item label={<b>Name</b>} key="name">
                  {assetAllocationRate?.name}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
